import { TEAM } from '../MatchAuthority.js';

/**
 * Domination — three capture points, score per second per point held.
 *
 * ## Where the points go
 *
 * The map was built long before there were game modes and knows nothing about
 * objectives. Hand-placing three coordinates would work exactly once and break
 * silently the first time a building moved, with the failure being "B is inside a
 * wall" discovered by a player who cannot capture it.
 *
 * So the flags are *derived* from geometry the level already guarantees: the spawn
 * points, which are validated as walkable and reachable at boot (`_collectSpawnPoints`
 * drops any the nav mesh cannot route to). Farthest-point sampling over that cloud
 * picks the two most distant, then the one furthest from both — which on any map
 * shaped like a map lands as "one at each end, one in the middle", the layout the
 * mode has always wanted.
 *
 * ## Why capture rules are shaped this way
 *
 * Neutralise-then-capture rather than a single sliding bar: it means taking a point
 * off a defended team costs twice, which is what stops a match from being decided
 * by whoever happens to be running fastest. Contested points freeze rather than
 * favouring the larger group, so a single defender can always deny — the defender's
 * one reliable move, and the reason holding two points is a real position instead
 * of a countdown.
 */

export const TUNING = {
  /** Metres. Big enough to fight inside, small enough that you cannot hold it from cover. */
  radius: 6.0,
  /** Vertical tolerance: a rooftop directly above a flag is not standing on it. */
  height: 3.2,
  /** Seconds for one soldier to take a neutral point. */
  captureTime: 7.0,
  /**
   * Extra capture speed per additional body, and the cap. Two soldiers are worth
   * 1.6 of one, not 2 — otherwise the whole team stacks one flag and the mode
   * becomes a single moving blob.
   */
  perExtra: 0.6,
  maxSpeed: 2.2,
  /** Seconds between score awards. */
  scoreInterval: 3.0,
  scoreLimit: 200,
  timeLimit: 900,
  squad: 6,
  zones: 3,
};

const LETTERS = ['A', 'B', 'C', 'D', 'E'];

export class Domination {
  constructor(opts = {}) {
    this.id = 'dom';
    this.name = 'ドミネーション';
    this.ownsVictory = true;
    this.friendlyFire = opts.friendlyFire ?? false;
    this.selfDamage = true;

    this.scoreLimit = opts.scoreLimit ?? TUNING.scoreLimit;
    this.timeLimit = opts.timeLimit ?? TUNING.timeLimit;
    this.squad = opts.squad ?? TUNING.squad;

    this.score = { [TEAM.ALPHA]: 0, [TEAM.BRAVO]: 0 };
    this.elapsed = 0;
    this.zones = [];
    this._scoreAcc = 0;
  }

  attach(match) {
    this.match = match;
    this.events = match.events;
    this._publish();
  }

  get roster() {
    return { [TEAM.ALPHA]: Math.max(0, this.squad - 1), [TEAM.BRAVO]: this.squad };
  }

  canDamage(src, dst) {
    if (this.friendlyFire) return true;
    if (src.team === TEAM.NONE || dst.team === TEAM.NONE) return true;
    return src.team !== dst.team;
  }

  canSplash() {
    return true;
  }

  /** Kills score nothing here. Holding ground does. */
  onKill() {}

  /* ------------------------------ the flags ------------------------------- */

  /**
   * Placed on the first tick rather than in the constructor: the rules object is
   * built before the AI director exists, and the director is what validates which
   * points are actually reachable.
   */
  _placeZones(ctx) {
    const pool = ctx.ai?.spawnPoints;
    if (!pool?.length) return false;

    const want = Math.min(TUNING.zones, pool.length);
    const picked = [];

    // Seed with the two furthest-apart points. O(n²) over a few dozen candidates,
    // once per match.
    let a = pool[0];
    let b = pool[0];
    let bestD = -1;
    for (let i = 0; i < pool.length; i++) {
      for (let j = i + 1; j < pool.length; j++) {
        const d = pool[i].distanceToSquared(pool[j]);
        if (d > bestD) {
          bestD = d;
          a = pool[i];
          b = pool[j];
        }
      }
    }
    picked.push(a, b);

    // Then repeatedly take whatever is furthest from everything chosen so far.
    while (picked.length < want) {
      let best = null;
      let bestMin = -1;
      for (const p of pool) {
        let nearest = Infinity;
        for (const q of picked) nearest = Math.min(nearest, p.distanceToSquared(q));
        if (nearest > bestMin) {
          bestMin = nearest;
          best = p;
        }
      }
      if (!best) break;
      picked.push(best);
    }

    // Order them along the line from the first to the second, so the letters run
    // across the map instead of in discovery order — "B" should be the middle one.
    const axis = picked[1].clone().sub(picked[0]);
    const len = axis.length() || 1;
    axis.multiplyScalar(1 / len);
    picked.sort((p, q) => p.dot(axis) - q.dot(axis));

    this.zones = picked.map((p, i) => ({
      id: LETTERS[i] ?? String(i),
      position: p.clone(),
      owner: TEAM.NONE,
      /** -1..1: which team is making progress, and how far. Sign is the team. */
      progress: 0,
      /** The team currently pushing the bar, or null. */
      capturing: null,
      contested: false,
      inside: { [TEAM.ALPHA]: 0, [TEAM.BRAVO]: 0 },
    }));
    this._publishZones();
    return true;
  }

  _countInside(zone) {
    const inside = zone.inside;
    inside[TEAM.ALPHA] = 0;
    inside[TEAM.BRAVO] = 0;
    const r2 = TUNING.radius * TUNING.radius;
    for (const reg of this.match.actors.values()) {
      if (reg.dead || typeof reg.actor.health !== 'number') continue;
      const p = reg.actor.position;
      if (!p) continue;
      const dy = p.y - zone.position.y;
      if (dy < -TUNING.height || dy > TUNING.height) continue;
      const dx = p.x - zone.position.x;
      const dz = p.z - zone.position.z;
      if (dx * dx + dz * dz > r2) continue;
      if (reg.team === TEAM.ALPHA || reg.team === TEAM.BRAVO) inside[reg.team]++;
    }
    return inside;
  }

  _stepZone(zone, dt) {
    const inside = this._countInside(zone);
    const a = inside[TEAM.ALPHA];
    const b = inside[TEAM.BRAVO];

    zone.contested = a > 0 && b > 0;
    if (zone.contested || (a === 0 && b === 0)) {
      zone.capturing = null;
      return;
    }

    const team = a > 0 ? TEAM.ALPHA : TEAM.BRAVO;
    const n = a > 0 ? a : b;
    const speed = Math.min(TUNING.maxSpeed, 1 + (n - 1) * TUNING.perExtra) / TUNING.captureTime;
    // Progress is signed so one number covers both "losing the enemy's flag" and
    // "taking it": it walks from -1 through 0 up to +1, and the owner flips at the
    // zero crossing. That is neutralise-then-capture with no second state machine.
    const dir = team === TEAM.ALPHA ? 1 : -1;
    zone.capturing = team;
    zone.progress = clamp(zone.progress + dir * speed * dt, -1, 1);

    const owner = zone.progress >= 1 ? TEAM.ALPHA : zone.progress <= -1 ? TEAM.BRAVO : null;
    if (owner !== null && owner !== zone.owner) {
      zone.owner = owner;
      this.events?.emit('objective:captured', {
        zone: zone.id,
        team: owner,
        position: zone.position.clone(),
      });
    } else if (owner === null && zone.owner !== TEAM.NONE) {
      // Dropped below the threshold in the other direction: the point is neutral
      // again, and the team that owned it stops scoring immediately.
      const stillOwned = zone.owner === TEAM.ALPHA ? zone.progress > 0 : zone.progress < 0;
      if (!stillOwned) {
        zone.owner = TEAM.NONE;
        this.events?.emit('objective:neutralised', { zone: zone.id, position: zone.position.clone() });
      }
    }
  }

  tick(dt, ctx) {
    if (!this.zones.length && !this._placeZones(ctx)) return;
    this.elapsed += dt;

    for (const z of this.zones) this._stepZone(z, dt);

    this._scoreAcc += dt;
    if (this._scoreAcc >= TUNING.scoreInterval) {
      this._scoreAcc -= TUNING.scoreInterval;
      let changed = false;
      for (const z of this.zones) {
        if (z.owner === TEAM.ALPHA || z.owner === TEAM.BRAVO) {
          this.score[z.owner]++;
          changed = true;
        }
      }
      if (changed) this._publish();
    }

    this._publishZones();
  }

  pickSpawn(reg, ctx) {
    // Spawn nearest a flag your side already holds, and failing that as far from
    // the enemy as the map allows. A mode about ground should put you on yours.
    const spawns = ctx.ai?.spawnPoints;
    if (!spawns?.length || !reg) return null;

    const held = this.zones.filter((z) => z.owner === reg.team);
    const foes = [];
    for (const r of this.match.actors.values()) {
      if (r.dead || r.team === reg.team || r.team === TEAM.NONE) continue;
      if (r.actor?.position) foes.push(r.actor);
    }

    let best = null;
    let bestScore = -Infinity;
    for (const s of spawns) {
      let nearestFoe = Infinity;
      for (const f of foes) nearestFoe = Math.min(nearestFoe, s.distanceTo(f.position));
      if (nearestFoe < 18) continue; // never in someone's lap
      let nearestHeld = Infinity;
      for (const z of held) nearestHeld = Math.min(nearestHeld, s.distanceTo(z.position));
      const score = (held.length ? -nearestHeld * 0.6 : 0) + Math.min(nearestFoe, 60) * 0.25;
      if (score > bestScore) {
        bestScore = score;
        best = s;
      }
    }
    return best;
  }

  checkWin() {
    for (const team of [TEAM.ALPHA, TEAM.BRAVO]) {
      if (this.score[team] >= this.scoreLimit) return this._verdict(team, 'score');
    }
    if (this.timeLimit > 0 && this.elapsed >= this.timeLimit) {
      const a = this.score[TEAM.ALPHA];
      const b = this.score[TEAM.BRAVO];
      if (a === b) return { team: TEAM.NONE, reason: 'time', key: 'banner.draw', win: false };
      return this._verdict(a > b ? TEAM.ALPHA : TEAM.BRAVO, 'time');
    }
    return null;
  }

  _verdict(team, reason) {
    const win = team === TEAM.ALPHA;
    return { team, reason, win, key: win ? 'banner.victory' : 'banner.defeat' };
  }

  _publish() {
    this.events?.emit('match:score', {
      mode: this.id,
      score: { ...this.score },
      limit: this.scoreLimit,
      elapsed: this.elapsed,
      timeLimit: this.timeLimit,
    });
  }

  _publishZones() {
    this.events?.emit('objective:zones', {
      zones: this.zones.map((z) => ({
        id: z.id,
        position: z.position,
        owner: z.owner,
        progress: z.progress,
        contested: z.contested,
        capturing: z.capturing,
        radius: TUNING.radius,
      })),
    });
  }
}

function clamp(v, lo, hi) {
  return v < lo ? lo : v > hi ? hi : v;
}
